import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import BackButton from '@/components/BackButton';
import Button from '@/components/Button';
import ContentSpinner from '@/components/ContentSpinner';
import { PageHeading } from '@/components/Layout/Parts';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { VideoCompilerCompilation } from '@/types/resources';
import { api } from '@/utils/api';

import Card from '../Create/components/Card';
import FileCard from '../Create/components/FileCard';
import Details from './components/Details';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 18px;

  ${PageHeading} {
    margin: 0 8px 0 0;
  }
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 16px;
`;

const Grid = styled.div`
  display: flex;
  gap: 16px;
  flex: 1;
  padding-bottom: 16px;
  overflow: hidden;
`;

const LeftCol = styled.div`
  width: 700px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RightCol = styled.div`
  flex: 1;
`;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: absolute;
  width: 100%;
  left: 0;
  top: 0;
`;

const Tips = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TipsTitle = styled(Text)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
`;

const Badge = styled(Text)`
  padding: 3px 8px;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 100px;
`;

const Tip = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Counter = styled(Text)`
  width: 32px;
  height: 21px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 100px;
`;

const File = styled.div`
  flex: 1;
`;

const ShowVideoCompilation = () => {
  const { compilationId } = useParams();

  const alerts = useAlerts();

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { data: compilation } = useQuery({
    queryFn: () =>
      api.get<never, VideoCompilerCompilation>(
        `/v1/video-compiler/compilations/${compilationId}`,
      ),
    queryKey: [
      'video_compiler_compilations',
      {
        id: compilationId,
      },
    ],
    enabled: !!compilationId,
  });

  const onDelete = async () => {
    try {
      const confirmed = await alerts.confirm('Are you sure?');

      if (!confirmed) {
        return;
      }

      await api.delete(`/v1/video-compiler/compilations/${compilation?.id}`);

      navigate('../', { relative: 'path', replace: true });

      await queryClient.invalidateQueries(['video_compiler_compilations']);

      alerts.close();
    } catch {
      alerts.error();
    }
  };

  if (!compilation) {
    return <ContentSpinner />;
  }

  return (
    <Page>
      <Text $type="caption" $color="gray">
        BORED PANDA
      </Text>
      <Header>
        <PageHeading>{compilation.title}</PageHeading>
        <HeaderButtons>
          <Button color="danger" onClick={onDelete}>
            Delete
          </Button>
        </HeaderButtons>
      </Header>
      <BackButton />
      <Grid>
        <LeftCol>
          <Card>
            <TipsTitle $type="smallBold">
              Files{' '}
              <Badge $type="smallBold" as="span">
                {compilation.parts!.length}
              </Badge>
            </TipsTitle>
            <Tips>
              {compilation.parts!.map((part) => (
                <Tip key={part.id}>
                  <Counter $type="smallHeadline" as="div">
                    {part.order}
                  </Counter>
                  <File>
                    <FileCard
                      part={
                        part.type === 'tip'
                          ? {
                              type: 'tip',
                              partId: part.id,
                              source: 'drive',
                              fileData: part.model.fileData,
                            }
                          : {
                              type: 'version',
                              source: 'local',
                              partId: part.id,
                              model: part.model,
                            }
                      }
                    />
                  </File>
                </Tip>
              ))}
            </Tips>
          </Card>
        </LeftCol>
        <RightCol>
          <Details compilation={compilation} />
        </RightCol>
      </Grid>
    </Page>
  );
};

export default ShowVideoCompilation;
